import React, { useState, useEffect } from "react";
import { NAV_LINKS } from "../data/mock";
import { Menu, X, Phone, Star, MapPin, Globe, Smartphone, Languages } from "lucide-react";
import { Button } from "./ui/button";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher, SUPPORTED_LANGS, FLAGS } from "./LanguageSwitcher";
import { useNavigate, useLocation } from "react-router-dom";

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLanguageChange = (newLang) => {
    setIsMobileMenuOpen(false);
    const segments = location.pathname.split("/").filter(Boolean);
    if (segments.length > 0 && SUPPORTED_LANGS.includes(segments[0])) {
      segments[0] = newLang;
    } else {
      segments.unshift(newLang);
    }
    const newPath = `/${segments.join("/")}${location.hash}`;
    navigate(newPath);
  };

  return (
    <>
      {/* Info Bar Superior */}
      <div className="bg-[#0C2340] text-white py-2 px-4 text-sm z-50 relative">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-6 flex-wrap">
          <span className="flex items-center gap-1.5">
            <Star className="w-3.5 h-3.5 text-[#C8A24D] fill-[#C8A24D]" />
            <span className="text-[#E8D5A0] font-medium">4.6/5</span>
            <span className="text-white/70">{t("header.rating")}</span>
          </span>
          <span className="hidden sm:inline text-white/30">|</span>
          <span className="hidden sm:flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 text-[#C8A24D]" />
            <span className="text-white/70">{t("header.headquarters")}</span>
          </span>
          <span className="hidden md:inline text-white/30">|</span>
          <span className="hidden md:flex items-center gap-1.5">
            <Globe className="w-3.5 h-3.5 text-[#C8A24D]" />
            <span className="text-white/70">{t("header.coverage")}</span>
          </span>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-white/90 backdrop-blur-2xl shadow-[0_2px_20px_rgba(12,35,64,0.08)] border-b border-[#E8E4DC]/50"
            : "bg-white/60 backdrop-blur-xl border-b border-[#E8E4DC]/50"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[72px]">
            {/* Logo */}
            <a href="#inicio" className="flex items-center gap-2 hover:opacity-90 transition-opacity">
              <div className="w-9 h-9 rounded-lg bg-[#0C2340] flex items-center justify-center shadow-sm">
                <span className="text-[#C8A24D] font-bold text-base" style={{ fontFamily: "'DM Serif Display', serif" }}>R</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[#0C2340] text-lg font-semibold tracking-tight leading-none" style={{ fontFamily: "'DM Serif Display', serif" }}>
                  ResidenciaYA
                </span>
                <span className="text-[10px] text-[#C8A24D] font-medium tracking-[0.2em] uppercase leading-none mt-0.5">.es</span>
              </div>
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.id}
                  href={`/${i18n.language}${link.href.startsWith('/') ? '' : '/'}${link.href}`}
                  className="px-4 py-2 text-sm font-medium text-[#4A5568] rounded-lg transition-colors duration-200 hover:text-[#0C2340] hover:bg-[#F5F1EA]"
                >
                  {t(`nav.${link.id}`)}
                </a>
              ))}
            </nav>

            {/* CTA & Language */}
            <div className="hidden lg:flex items-center gap-3">
              <a 
                href="https://chat.whatsapp.com/IaJxJHXftmyJ5KDcgaTspC" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group flex items-center gap-2 px-4 py-2 text-sm font-semibold text-[#25D366] border border-[#25D366]/30 rounded-lg hover:bg-[#25D366]/10 transition-all duration-200"
              >
                <Smartphone className="w-4 h-4" />
                <span>{t("header.whatsapp")}</span>
              </a>
              <Button
                className="bg-[#C8A24D] hover:bg-[#B8920D] text-[#0C2340] font-semibold text-sm h-10 px-5 rounded-lg shadow-md shadow-[#C8A24D]/20 transition-all duration-200 hover:shadow-lg hover:shadow-[#C8A24D]/30"
                onClick={() => document.getElementById('viabilidad')?.scrollIntoView({ behavior: 'smooth' })}
              >
                {t("header.cta")}
              </Button>
              <LanguageSwitcher />
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg text-[#4A5568] hover:bg-[#F5F1EA] transition-colors"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-[#E8E4DC] animate-fade-in">
            <div className="px-4 py-4 space-y-1">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.id}
                  href={`/${i18n.language}${link.href.startsWith('/') ? '' : '/'}${link.href}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-4 py-3 text-sm font-medium text-[#4A5568] rounded-lg hover:bg-[#F5F1EA] hover:text-[#0C2340] transition-colors"
                >
                  {t(`nav.${link.id}`)}
                </a>
              ))}

              <div className="pt-3 space-y-2 border-t border-[#E8E4DC] mt-3">
                <div className="flex flex-col px-1 pb-2">
                  <span className="text-xs font-medium text-[#7A7A8A] uppercase tracking-wider mb-2 ml-1">
                    {t("header.langLabel")}
                  </span>
                  <div className="grid grid-cols-2 gap-2">
                    {SUPPORTED_LANGS.map(l => (
                      <button
                        key={l}
                        onClick={() => handleLanguageChange(l)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${i18n.language === l ? "bg-[#C8A24D]/10 border-[#C8A24D]/30 text-[#0C2340]" : "bg-white border-[#E8E4DC] text-[#4A5568] hover:bg-[#F5F1EA]"}`}
                      >
                        <span className="text-base">{FLAGS[l]?.flag}</span>
                        {FLAGS[l]?.label}
                      </button>
                    ))}
                  </div>
                </div>
                <a 
                  href="https://chat.whatsapp.com/IaJxJHXftmyJ5KDcgaTspC" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full h-11 text-sm font-semibold text-[#25D366] border border-[#25D366]/30 rounded-lg hover:bg-[#25D366]/10 transition-all duration-200"
                >
                  <Smartphone className="w-4 h-4" />
                  {t("header.whatsapp")}
                </a>
                <Button className="w-full bg-[#C8A24D] hover:bg-[#B8920D] text-[#0C2340] font-semibold h-11 rounded-lg"
                  onClick={() => { setIsMobileMenuOpen(false); document.getElementById('viabilidad')?.scrollIntoView({ behavior: 'smooth' }); }}
                >
                  {t("header.cta")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
